/**
 * TravelBufferSelfCheck — tiny optional sanity-check affordance
 * on the scheduling-prefs page.
 *
 * Phase B2 follow-on (Feb 18, 2026). Per operator directive:
 *
 *   "Optional only. Calm tone. No onboarding flow. No wizard.
 *    No persistent nags. Pure read-only sanity check. Two
 *    sample postcodes / example gap. Show resulting pressure
 *    band + explanation. Reinforce: this helps preview how
 *    your travel buffer behaves."
 *
 * What this component does:
 *   • Renders a small "Run sanity check" button.
 *   • On tap, calls `GET /api/scheduling/travel-feasibility`
 *     with two operator-fixed sample postcodes (SW19 → SW15)
 *     + a 60-min gap. The instructor's saved buffer is used
 *     automatically (server-side resolution).
 *   • Displays one line of explanation + pressure band + math
 *     breakdown. A "Hide" button returns to the idle state.
 *
 * What this component does NOT do:
 *   • Does NOT auto-tune the buffer.
 *   • Does NOT recommend business decisions.
 *   • Does NOT persist anywhere — pure transient UI state.
 *   • Does NOT score the instructor.
 *   • Does NOT loop, retry, or nag.
 */
import { useState } from "react";
import axios from "axios";
import { Compass } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Operator-fixed sample. SW19 → SW15 is a known-comfortable
// short London hop (~3 mi at 25 mph).
const SAMPLE = {
  from_postcode:           "SW19 1AA",
  to_postcode:             "SW15 6AA",
  adjacent_end_iso:        "2026-03-01T09:00:00+00:00",
  slot_start_iso:          "2026-03-01T10:00:00+00:00",
  slot_duration_minutes:   60,
};

const BAND_LABEL = {
  comfortable: "Comfortable",
  tight:       "Tight",
  impossible:  "Impossible",
  unknown:     "Unknown",
};

export default function TravelBufferSelfCheck() {
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);

  async function run() {
    if (loading) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(SAMPLE).forEach(([k, v]) =>
        params.set(k, String(v)),
      );
      const r = await axios.get(
        `${API}/api/scheduling/travel-feasibility?${params.toString()}`,
        { withCredentials: true },
      );
      setResult(r.data);
    } catch {
      setResult({ pressure_band: "unknown",
                  summary_line: "Could not reach the calculator." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="travel-buffer-self-check"
      className="bg-slate-50 border border-slate-200 rounded-2xl p-5"
    >
      <header className="flex items-center gap-2 mb-2">
        <Compass className="w-4 h-4 text-slate-400" />
        <p className="text-sm font-bold text-slate-700">
          Travel buffer sanity check
        </p>
      </header>
      <p className="text-[12px] text-slate-500 leading-relaxed mb-3">
        Optional. Previews how your travel buffer behaves with a
        sample {SAMPLE.from_postcode} → {SAMPLE.to_postcode} gap.
        Nothing is saved.
      </p>

      {!result && (
        <button
          type="button"
          data-testid="travel-buffer-self-check-run"
          onClick={run}
          disabled={loading}
          className="text-sm font-bold text-slate-700 px-4 py-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50"
        >
          {loading ? "Checking…" : "Run sanity check"}
        </button>
      )}

      {result && (
        <div
          data-testid="travel-buffer-self-check-result"
          className="text-sm text-slate-700 space-y-2"
        >
          <p>
            Pressure band:{" "}
            <span
              data-testid="travel-buffer-self-check-band"
              className="font-bold tabular-nums"
            >
              {BAND_LABEL[result.pressure_band] || result.pressure_band}
            </span>
          </p>
          <p className="text-[12px] text-slate-500 leading-relaxed">
            {result.summary_line}
          </p>
          {(result.required_gap_minutes !== null
            && result.required_gap_minutes !== undefined) && (
            <p className="text-[11px] text-slate-400 leading-relaxed font-mono">
              {(result.distance_miles ?? 0)} mi at{" "}
              {(result.speed_mph ?? 0)} mph
              {" · "}+ {result.overhead_minutes ?? 0} min overhead
              {" · "}+ {result.buffer_minutes ?? 0} min buffer
              {" = "}
              {result.required_gap_minutes} min total
            </p>
          )}
          <button
            type="button"
            data-testid="travel-buffer-self-check-hide"
            onClick={() => setResult(null)}
            className="text-[12px] font-bold text-slate-600 px-2 py-1 hover:text-slate-900"
          >
            Hide
          </button>
        </div>
      )}
    </div>
  );
}
